import {
  kbEmbedResponse,
  kbSearchRequest,
  kbSearchResponse,
  triggerApproveEpic,
  triggerConfirmSprint,
  triggerEnqueuedResponse,
  triggerFeatureCreated,
  type KbEmbedResponse,
  type KbSearchRequest,
  type KbSearchResponse,
  type TriggerApproveEpic,
  type TriggerConfirmSprint,
  type TriggerEnqueuedResponse,
  type TriggerFeatureCreated,
} from '@repo-manager/shared';
import type { ZodSchema } from 'zod';

import { env } from './env';
import { getSupabase, type SupabaseBrowserClient } from './supabase';

/** Worker call failed. `status` echoes the HTTP code (or 0 for transport). */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Json = Record<string, unknown>;
type TokenGetter = () => Promise<string | null>;

async function call<T>(
  path: string,
  body: Json,
  schema: ZodSchema<T>,
  requestSchema: ZodSchema<unknown> | null,
  getToken: TokenGetter,
): Promise<T> {
  if (requestSchema) {
    // Trust-boundary validation: also Zod-check what we're about to send.
    requestSchema.parse(body);
  }
  const token = await getToken();
  if (!token) throw new ApiError('not_authenticated', 401);

  const url = `${env.VITE_WORKER_URL}${path}`;
  const headers: HeadersInit = {
    authorization: `Bearer ${token}`,
    'content-type': 'application/json',
  };

  const attempt = () =>
    fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });

  let res: Response;
  try {
    res = await attempt();
    // One retry on transient 5xx; bail on 4xx.
    if (res.status >= 500 && res.status < 600) {
      res = await attempt();
    }
  } catch (e) {
    throw new ApiError(e instanceof Error ? e.message : 'transport_error', 0);
  }

  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = await res.text().catch(() => '');
    }
    throw new ApiError(`request_failed:${res.status}`, res.status, errBody);
  }

  const json: unknown = await res.json();
  return schema.parse(json);
}

export interface RepoSummary {
  id: string;
  owner: string;
  name: string;
  last_active_at: string | null;
  kb_version_hash: string | null;
  github_repo_id: string | number | null;
  github_installation_id: string | number | null;
  // Other columns exist (e.g. created_at); the dashboard reads what's named.
  [key: string]: unknown;
}

export interface RepoStatusBlob {
  agent_count?: number;
  pending_pr_count?: number;
  active_sprint_id?: string | null;
  last_decision_id?: string | null;
  envs?: Record<string, string>;
  generated_at?: string;
  [key: string]: unknown;
}

export interface HandoffSummary {
  current_state: string;
  in_flight: string;
  open_questions: string;
  next_actions: string;
  context_notes: string;
  created_at: string;
  active_sprint_id: string | null;
  agent_count: number;
  pending_pr_count: number;
}

export interface ApiClient {
  kbSearch: (req: KbSearchRequest) => Promise<KbSearchResponse>;
  kbEmbed: (slugs?: string[]) => Promise<KbEmbedResponse>;
  triggerFeatureCreated: (body: TriggerFeatureCreated) => Promise<TriggerEnqueuedResponse>;
  triggerApproveEpic: (body: TriggerApproveEpic) => Promise<TriggerEnqueuedResponse>;
  triggerConfirmSprint: (body: TriggerConfirmSprint) => Promise<TriggerEnqueuedResponse>;
  /** Phase 10 — operator/ops endpoints surfaced for the multi-repo dashboard. */
  reposList: () => Promise<{ repos: RepoSummary[] }>;
  reposStatus: (id: string) => Promise<{ repo_id: string; status: RepoStatusBlob | null }>;
  reposHandoff: (id: string) => Promise<{ repo_id: string; handoff: HandoffSummary } | null>;
}

/**
 * GET helper for the new multi-repo dashboard. Mirrors `call()` above
 * but uses GET (no body) and skips request-schema validation since
 * the path is opaque.
 */
async function getJson<T>(path: string, getToken: TokenGetter): Promise<T> {
  const token = await getToken();
  if (!token) throw new ApiError('not_authenticated', 401);
  const url = `${env.VITE_WORKER_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(e instanceof Error ? e.message : 'transport_error', 0);
  }
  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = await res.text().catch(() => '');
    }
    throw new ApiError(`request_failed:${res.status}`, res.status, errBody);
  }
  return (await res.json()) as T;
}

export function makeApi(supabase: SupabaseBrowserClient): ApiClient {
  const getToken: TokenGetter = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  };
  return {
    kbSearch: (req) => call('/api/kb/search', req, kbSearchResponse, kbSearchRequest, getToken),
    kbEmbed: (slugs) =>
      call('/api/kb/embed', slugs ? { slugs } : {}, kbEmbedResponse, null, getToken),
    triggerFeatureCreated: (body) =>
      call('/api/trigger/feature-created', body, triggerEnqueuedResponse, triggerFeatureCreated, getToken),
    triggerApproveEpic: (body) =>
      call('/api/trigger/approve-epic', body, triggerEnqueuedResponse, triggerApproveEpic, getToken),
    triggerConfirmSprint: (body) =>
      call('/api/trigger/confirm-sprint', body, triggerEnqueuedResponse, triggerConfirmSprint, getToken),
    reposList: () => getJson('/api/repos', getToken),
    reposStatus: (id) => getJson(`/api/repos/${id}/status`, getToken),
    reposHandoff: async (id) => {
      try {
        return await getJson(`/api/repos/${id}/handoff`, getToken);
      } catch (e) {
        // 404 == no handoff yet for this repo (fresh / first-cycle). The
        // dashboard treats that as "no data" rather than an error.
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  };
}

/**
 * Singleton api client built against the Supabase singleton. Use from
 * route loaders/actions where React context isn't available; inside
 * components prefer `useApi()` from `supabase-context.tsx` for
 * consistency.
 */
let _api: ApiClient | null = null;
export function getApi(): ApiClient {
  if (!_api) _api = makeApi(getSupabase());
  return _api;
}
